import { useMemo } from 'react';
import { BuildingType } from '../../types/BuildingTypeEnums';
import { BuildingSummary } from '../../types/protocols';
import { todayStockholm } from '../../Helpers/leaseUtils';
import { DateRange, ServiceType } from '../../Pages/Analysis/types/analysis.types';
import { extractServiceLogEntries } from '../../Pages/Analysis/utils/dataProcessing';
import {
    ChartRow,
    ComparisonGrouping,
    ComparisonMetric,
    cumulativeSeries,
    cutSpan,
    DayMonth,
    sameDateCutOff,
    yearSpan,
    yearTotals,
    YearTotals,
} from '../../Pages/Analysis/utils/yearComparison';

/** Jämför år compares whole calendar years */
const YEAR_START: DayMonth = { month: 1, day: 1 };
const YEAR_END: DayMonth = { month: 12, day: 31 };

/** The `buildingServices` node as Analys fetches it */
export interface BuildingServicesData {
    servicesLogs?: Record<string, unknown>;
}

interface UseYearComparisonArgs {
    servicesData: BuildingServicesData | null;
    buildings: BuildingSummary[];
    buildingTypes: BuildingType[];
    userGroup: string | null;
    /** Picked buildings; empty = all */
    buildingKeys: string[];
    serviceTypes: ServiceType[];
    /** Oldest first */
    years: number[];
    /** Cut every year at the point the current season has reached today */
    sameDate: boolean;
    grouping: ComparisonGrouping;
    metric: ComparisonMetric;
}

export interface YearColumn {
    year: number;
    /** The span the table counted (cut when the same-date cut-off is on) */
    span: DateRange;
    totals: YearTotals;
}

export interface YearComparison {
    columns: YearColumn[];
    chartRows: ChartRow[];
    /** Today, when the current year is selected; the cut-off checkbox shows only then */
    cutOffDate: string | null;
}

/**
 * Derives the "Jämför år" table and chart from the already fetched service
 * logs. Read-only: no fetch of its own.
 */
export const useYearComparison = ({
    servicesData,
    buildings,
    buildingTypes,
    userGroup,
    buildingKeys,
    serviceTypes,
    years,
    sameDate,
    grouping,
    metric,
}: UseYearComparisonArgs): YearComparison => {
    const today = useMemo(() => todayStockholm(), []);

    const perYear = useMemo(() => {
        if (!servicesData) return null;
        const cutOff = sameDateCutOff(today, YEAR_START, YEAR_END);
        return {
            cutOff: cutOff && years.includes(cutOff.seasonYear) ? cutOff : null,
            years: years.map(year => {
                const span = yearSpan(year, YEAR_START, YEAR_END);
                const entries = extractServiceLogEntries(servicesData, span, buildings, buildingTypes, userGroup, false, buildingKeys);
                return { year, span, entries };
            }),
        };
    }, [servicesData, buildings, buildingTypes, userGroup, buildingKeys, years, today]);

    const columns = useMemo<YearColumn[]>(() => {
        if (!perYear) return [];
        return perYear.years.map(({ year, span, entries }) => {
            const counted = sameDate && perYear.cutOff ? cutSpan(span, perYear.cutOff.offsetDays) : span;
            const inSpan = entries.filter(entry => entry.date >= counted.start && entry.date <= counted.end);
            return { year, span: counted, totals: yearTotals(inSpan, serviceTypes) };
        });
    }, [perYear, sameDate, serviceTypes]);

    const chartRows = useMemo<ChartRow[]>(() => {
        if (!perYear) return [];
        // A season still running stops at today; earlier years always run their full span
        const series = perYear.years.map(year => ({
            ...year,
            lastDate: year.span.end > today ? today : undefined,
        }));
        return cumulativeSeries(series, metric, serviceTypes, grouping);
    }, [perYear, metric, serviceTypes, grouping, today]);

    return {
        columns,
        chartRows,
        cutOffDate: perYear?.cutOff ? today : null,
    };
};
